import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Card,
  Divider,
  Input,
  InputNumber,
  Select,
  Table,
  Tag,
  Tooltip
} from 'antd'
import moment from 'moment'
import { getMaterial } from '../../modules/material'
import {
  CUSTOM_SORT,
  getCustomMaterialList
} from '../../modules/materialCustom'
import ReparentModal from './components/ReparentModal'
import PromoteForm from './components/PromoteForm'

const PAGE_SIZE = 20

const materialLabel = (material) =>
  material.commonName
    ? `${material.name} (${material.commonName})`
    : `${material.name}`

// วัสดุที่ร้านสร้าง: แอดมินย้ายที่ผูก หรือยกเป็นวัสดุมาตรฐาน (spec 017 D1–D9)
const MaterialCustom = () => {
  const dispatch = useDispatch()
  const [filters, setFilters] = useState({
    keyword: '',
    parentMaterialId: undefined,
    heroId: undefined,
    sort: CUSTOM_SORT.SAME_NAME
  })
  const [keywordInput, setKeywordInput] = useState('')
  const [page, setPage] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [reparentRows, setReparentRows] = useState([])
  const [promoteRow, setPromoteRow] = useState(null)

  const { customList, isLoading, materials } = useSelector(
    (state) => ({
      customList: state.materialCustom.customList,
      isLoading: state.materialCustom.isLoading,
      materials: state.material.material.data
    }),
    []
  )

  const load = (nextFilters, nextPage) => {
    setFilters(nextFilters)
    setPage(nextPage)
    // เลือกแถวได้เฉพาะในหน้าที่เห็นอยู่ โหลดใหม่เมื่อไรให้ล้างรายการที่เลือก
    setSelectedRowKeys([])
    return dispatch(
      getCustomMaterialList({
        keyword: nextFilters.keyword || undefined,
        parentMaterialId: nextFilters.parentMaterialId,
        heroId: nextFilters.heroId,
        sort: nextFilters.sort,
        page: nextPage,
        size: PAGE_SIZE
      })
    )
  }

  useEffect(() => {
    load(filters, 0)
    dispatch(getMaterial())
    // โหลดครั้งแรกเมื่อเปิดหน้า
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rows = customList.content || []
  const selectedRows = rows.filter((row) => selectedRowKeys.includes(row.id))

  const onCloseReparent = (changed) => {
    setReparentRows([])
    if (changed) {
      load(filters, page)
    }
  }

  const onClosePromote = (changed) => {
    setPromoteRow(null)
    if (changed) {
      // แถวที่ยกแล้วหายจากหน้านี้ ถ้าเป็นแถวสุดท้ายของหน้าให้ถอยกลับหนึ่งหน้า
      load(filters, rows.length === 1 && page > 0 ? page - 1 : page)
    }
  }

  const columns = [
    {
      title: 'ชื่อ',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <div>
          {row.productImage && (
            <img
              src={row.productImage}
              width={48}
              height={48}
              style={{ marginRight: 10, objectFit: 'cover' }}
              alt="Product"
            />
          )}
          {name}
        </div>
      )
    },
    {
      title: 'ร้านที่ตั้งชื่อเดียวกัน',
      dataIndex: 'sameNameShopCount',
      key: 'sameNameShopCount',
      width: 160,
      align: 'center',
      render: (count) =>
        count > 1 ? <Tag color="orange">{count} ร้าน</Tag> : count
    },
    {
      title: 'ร้าน',
      dataIndex: 'ownerHeroName',
      key: 'ownerHeroName',
      render: (ownerHeroName, row) => (
        <div>
          <div>{ownerHeroName || '-'}</div>
          <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
            Hero ID {row.ownerHeroId}
          </div>
        </div>
      )
    },
    {
      title: 'ผูกกับ',
      dataIndex: 'parentMaterialName',
      key: 'parentMaterialName',
      render: (parentMaterialName) => parentMaterialName || '-'
    },
    {
      title: 'หน่วย',
      dataIndex: 'unit',
      key: 'unit',
      width: 90
    },
    {
      title: 'สถานะ',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      align: 'center',
      render: (active) =>
        active ? <Tag color="green">ใช้งาน</Tag> : <Tag>ปิด</Tag>
    },
    {
      title: 'หมวด',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 150
    },
    {
      title: 'วันที่สร้าง',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (value) =>
        value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 250,
      render: (text, row) => (
        <div>
          <Button size="small" onClick={() => setReparentRows([row])}>
            ย้ายที่ผูก
          </Button>
          <Divider type="vertical" />
          <Tooltip
            title={
              row.active ? '' : 'ร้านปิดสินค้านี้แล้ว จึงยกเป็นมาตรฐานไม่ได้'
            }>
            <Button
              type="primary"
              size="small"
              disabled={!row.active}
              onClick={() => setPromoteRow(row)}>
              ยกเป็นวัสดุมาตรฐาน
            </Button>
          </Tooltip>
        </div>
      )
    }
  ]

  return (
    <Card
      title="Shop Materials"
      extra={
        <Button size="small" icon="reload" onClick={() => load(filters, page)}>
          Reload
        </Button>
      }>
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}>
        <Input.Search
          style={{ width: 220, marginRight: 8, marginBottom: 8 }}
          placeholder="ค้นจากชื่อ"
          allowClear
          value={keywordInput}
          onChange={(e) => {
            const { value } = e.target
            setKeywordInput(value)
            // ล้างช่องค้น (ปุ่มกากบาทหรือลบจนว่าง) ให้กลับไปแสดงทั้งหมดทันที
            if (!value && filters.keyword) {
              load({ ...filters, keyword: '' }, 0)
            }
          }}
          onSearch={(value) => load({ ...filters, keyword: value.trim() }, 0)}
        />
        <Select
          showSearch
          allowClear
          style={{ width: 260, marginRight: 8, marginBottom: 8 }}
          placeholder="วัสดุที่ผูก"
          value={filters.parentMaterialId}
          onChange={(value) => load({ ...filters, parentMaterialId: value }, 0)}
          filterOption={(input, option) =>
            String(option.props.children)
              .toLowerCase()
              .includes(input.toLowerCase())
          }>
          {(materials || []).map((material) => (
            <Select.Option key={material.id} value={material.id}>
              {materialLabel(material)}
            </Select.Option>
          ))}
        </Select>
        <InputNumber
          style={{ width: 130, marginRight: 8, marginBottom: 8 }}
          placeholder="Hero ID"
          min={1}
          value={filters.heroId}
          onChange={(value) =>
            load({ ...filters, heroId: value || undefined }, 0)
          }
        />
        <Select
          style={{ width: 240, marginRight: 8, marginBottom: 8 }}
          value={filters.sort}
          onChange={(value) => load({ ...filters, sort: value }, 0)}>
          <Select.Option value={CUSTOM_SORT.SAME_NAME}>
            เรียงตามร้านที่ตั้งชื่อเดียวกัน
          </Select.Option>
          <Select.Option value={CUSTOM_SORT.CREATED_AT}>
            เรียงตามวันที่สร้าง (ใหม่ก่อน)
          </Select.Option>
        </Select>
        <Button
          type="primary"
          style={{ marginBottom: 8 }}
          disabled={selectedRows.length === 0}
          onClick={() => setReparentRows(selectedRows)}>
          ย้ายที่ผูกทั้งหมด
          {selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
        </Button>
      </div>
      <Table
        loading={isLoading}
        scroll={{ x: 1400 }}
        rowKey={(row) => row.id}
        dataSource={rows}
        columns={columns}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys
        }}
        pagination={{
          current: page + 1,
          pageSize: PAGE_SIZE,
          total: customList.total,
          onChange: (nextPage) => load(filters, nextPage - 1)
        }}
      />
      <ReparentModal
        visible={reparentRows.length > 0}
        rows={reparentRows}
        materials={materials}
        onClose={onCloseReparent}
      />
      <PromoteForm
        visible={!!promoteRow}
        row={promoteRow}
        onClose={onClosePromote}
      />
    </Card>
  )
}

export default MaterialCustom
